/**
 * Logout Service
 * Centralized logout functionality with proper error handling and user feedback
 */

import { getApiBaseUrl } from '../config/api';

/**
 * Clear all authentication-related storage
 */
const clearAuthStorage = (userType = 'agent') => {
  const storageKeys = {
    agent: ['agentToken', 'agentUser'],
    employee: ['employeeToken', 'employeeUser', 'isEmployee', 'employeeMustResetPassword'],
    admin: ['adminToken', 'adminUser']
  };

  const keysToRemove = storageKeys[userType] || storageKeys.agent;
  
  // Clear localStorage
  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      // Storage might be disabled or full - continue anyway
    }
  });

  // Clear sessionStorage
  try {
    sessionStorage.clear();
  } catch (error) {
    // Continue if sessionStorage is not available
  }

  // Clear cookies
  const cookiesToClear = ['agentToken', 'agentUser', 'employeeToken', 'employeeUser', 'adminToken', 'adminUser', 'sessionId', 'csrfToken'];
  cookiesToClear.forEach(cookieName => {
    try {
      const hostname = window.location.hostname;
      const paths = ['/', window.location.pathname];
      const domains = [hostname, `.${hostname}`, ''];
      
      paths.forEach(path => {
        domains.forEach(domain => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain};`;
        });
      });
    } catch (error) {
      // Continue if cookie clearing fails
    }
  });
};

/**
 * Clear browser caches
 */
const clearCaches = async () => {
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(name => caches.delete(name))
      );
    }
  } catch (error) {
    // Cache clearing is optional - continue
  }
};

/**
 * Clear IndexedDB
 */
const clearIndexedDB = async () => {
  try {
    if ('indexedDB' in window) {
      const databases = await indexedDB.databases();
      await Promise.all(
        databases
          .filter(db => db.name && (
            db.name.includes('agent') || 
            db.name.includes('employee') || 
            db.name.includes('admin')
          ))
          .map(db => indexedDB.deleteDatabase(db.name))
      );
    }
  } catch (error) {
    // IndexedDB clearing is optional - continue
  }
};

/**
 * Notify server about logout (fire and forget)
 */
const notifyServerLogout = async (token, userType = 'agent') => {
  if (!token) return;

  try {
    // Use silent mode to avoid console warnings during logout
    const API_BASE_URL = getApiBaseUrl(true);
    const endpoint = userType === 'employee' 
      ? '/api/employee/logout' 
      : userType === 'admin'
      ? '/api/admin/logout'
      : '/api/agent/logout';

    // Fire and forget - don't wait for response
    fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      signal: AbortSignal.timeout(3000) // 3 second timeout
    }).catch(() => {
      // Silently ignore - we're logging out anyway
    });
  } catch (error) {
    // Silently ignore - continue with logout
  }
};

/**
 * Main logout function
 * @param {Object} options - Logout options
 * @param {string} options.userType - 'agent', 'employee', or 'admin'
 * @param {Function} options.onSuccess - Callback on successful logout
 * @param {Function} options.onError - Callback on error (optional)
 * @param {boolean} options.showNotification - Whether to show notification (default: true)
 * @param {Function} options.showNotificationFn - Function to show notification
 * @returns {Promise<void>}
 */
export const performLogout = async ({
  userType = 'agent',
  onSuccess,
  onError,
  showNotification = true,
  showNotificationFn
} = {}) => {
  try {
    // Get token before clearing storage
    const tokenKey = userType === 'employee' 
      ? 'employeeToken' 
      : userType === 'admin'
      ? 'adminToken'
      : 'agentToken';
    
    const token = localStorage.getItem(tokenKey);

    // Notify server (fire and forget)
    if (token) {
      await notifyServerLogout(token, userType);
    }

    // Clear all storage
    clearAuthStorage(userType);

    // Clear caches (async, don't wait)
    clearCaches().catch(() => {});
    clearIndexedDB().catch(() => {});

    // Show success notification if callback provided
    if (showNotification && showNotificationFn) {
      showNotificationFn('Logged out successfully', 'success');
    }

    // Call success callback
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    // Even if there's an error, we still clear local storage
    clearAuthStorage(userType);

    // Show error notification if callback provided
    if (showNotification && showNotificationFn) {
      showNotificationFn('Logged out (some cleanup may have failed)', 'warning');
    }

    // Call error callback if provided
    if (onError) {
      onError(error);
    } else if (onSuccess) {
      // If no error handler, still call success to complete logout
      onSuccess();
    }
  }
};

/**
 * Logout with navigation
 * @param {Object} options - Logout options
 * @param {string} options.userType - 'agent', 'employee', or 'admin'
 * @param {Function} options.navigate - React Router navigate function
 * @param {string} options.redirectPath - Path to redirect after logout
 * @param {Function} options.showNotificationFn - Function to show notification
 * @returns {Promise<void>}
 */
export const logoutAndNavigate = async ({
  userType = 'agent',
  navigate,
  redirectPath = '/',
  showNotificationFn
} = {}) => {
  await performLogout({
    userType,
    showNotification: !!showNotificationFn,
    showNotificationFn,
    onSuccess: () => {
      if (navigate) {
        navigate(redirectPath);
      } else {
        // Fallback to window location if navigate not provided
        window.location.href = redirectPath;
      }
    }
  });
};

export default {
  performLogout,
  logoutAndNavigate,
  clearAuthStorage,
  clearCaches,
  clearIndexedDB
};
